import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { GasThread, GasThreadRequestBody, GasThreadViewModel } from './types';

export const gasThreadApi = createApi({
  reducerPath: 'gasThreadApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/gas-threads',
  }),
  tagTypes: ['GasThread', 'GasThreads'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getGasThreadsI: builder.infiniteQuery<
      GasThreadViewModel[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['GasThreads'],
    }),
    getGasThreadById: builder.query<GasThread, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['GasThread'],
    }),
    createGasThread: builder.mutation<string, GasThreadRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: id => ['GasThreads', { type: 'GasThread', id }],
    }),
    updateGasThread: builder.mutation<GasThread, GasThreadRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'GasThreads',
        { type: 'GasThread', id: result?.id },
      ],
    }),
  }),
});

export const {
  useLazyGetGasThreadByIdQuery,
  useGetGasThreadByIdQuery,
  useCreateGasThreadMutation,
  useUpdateGasThreadMutation,
} = gasThreadApi;

export const useGetGasThreadsInfiniteQuery = normalizeInfiniteQueryData(
  gasThreadApi.useGetGasThreadsIInfiniteQuery,
);

const getCachedGasThreadByIdSelector = cachedItemByIdSelector(
  'gasThreadApi',
  'getGasThreadsI',
);

export const useGetCachedGasThreadByIdSelector = id =>
  useSelector(getCachedGasThreadByIdSelector(id));
